import React, { useState, useEffect } from 'react';
import './PowerManagement.css';

/**
 * Power Management Settings Component
 * Allows users to configure power-saving options
 */
const PowerManagement = () => {
  const [settings, setSettings] = useState({
    powerSaveMode: false,
    balanceUpdateInterval: 30000, // 30 seconds default
    enableVisibilityOptimization: true,
    enableBatteryOptimization: true,
    pauseOnLowBattery: true,
    lowBatteryThreshold: 20,
    reduceAnimations: false,
    limitWebSocketConnections: false,
    enableBackgroundSync: true
  });

  const [batteryInfo, setBatteryInfo] = useState({
    level: 1,
    charging: true,
    supported: false
  });

  // Load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('powerManagementSettings');
    if (savedSettings) {
      setSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
    }
  }, []);

  // Get battery information
  useEffect(() => {
    if (navigator.getBattery) {
      navigator.getBattery().then(battery => {
        setBatteryInfo({
          level: Math.round(battery.level * 100),
          charging: battery.charging,
          supported: true
        });

        const updateBatteryInfo = () => {
          setBatteryInfo({
            level: Math.round(battery.level * 100),
            charging: battery.charging,
            supported: true
          });
        };

        battery.addEventListener('levelchange', updateBatteryInfo);
        battery.addEventListener('chargingchange', updateBatteryInfo);

        return () => {
          battery.removeEventListener('levelchange', updateBatteryInfo);
          battery.removeEventListener('chargingchange', updateBatteryInfo);
        };
      });
    }
  }, []);

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('powerManagementSettings', JSON.stringify(newSettings));
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('powerSettingsChanged', { 
      detail: newSettings 
    }));
  };

  const getUpdateIntervalLabel = (interval) => {
    if (interval >= 60000) {
      return `${interval / 60000} minute${interval > 60000 ? 's' : ''}`;
    }
    return `${interval / 1000} seconds`;
  };

  const getBatteryIcon = () => {
    if (!batteryInfo.supported) return '🔋';
    
    if (batteryInfo.charging) return '🔌';
    
    if (batteryInfo.level > 75) return '🔋';
    if (batteryInfo.level > 50) return '🔋';
    if (batteryInfo.level > 25) return '🪫';
    return '🪫';
  };

  const getBatteryColor = () => {
    if (batteryInfo.charging) return '#4CAF50';
    if (batteryInfo.level > 50) return '#4CAF50';
    if (batteryInfo.level > 25) return '#FF9800';
    return '#F44336';
  };

  const presets = {
    performance: {
      powerSaveMode: false,
      balanceUpdateInterval: 30000,
      enableVisibilityOptimization: false,
      enableBatteryOptimization: false,
      pauseOnLowBattery: false,
      reduceAnimations: false,
      limitWebSocketConnections: false,
      enableBackgroundSync: true
    },
    balanced: {
      powerSaveMode: false,
      balanceUpdateInterval: 60000,
      enableVisibilityOptimization: true,
      enableBatteryOptimization: true,
      pauseOnLowBattery: true,
      lowBatteryThreshold: 20,
      reduceAnimations: false,
      limitWebSocketConnections: false,
      enableBackgroundSync: true
    },
    powerSaver: {
      powerSaveMode: true,
      balanceUpdateInterval: 120000,
      enableVisibilityOptimization: true,
      enableBatteryOptimization: true,
      pauseOnLowBattery: true,
      lowBatteryThreshold: 30,
      reduceAnimations: true,
      limitWebSocketConnections: true,
      enableBackgroundSync: false
    }
  };

  const applyPreset = (presetName) => {
    const preset = presets[presetName];
    setSettings(prev => ({ ...prev, ...preset }));
    localStorage.setItem('powerManagementSettings', JSON.stringify({ ...settings, ...preset }));
    
    window.dispatchEvent(new CustomEvent('powerSettingsChanged', { 
      detail: { ...settings, ...preset }
    }));
  };

  return (
    <div className="power-management">
      <div className="power-header">
        <h3>⚡ Power Management</h3>
        {batteryInfo.supported && (
          <div className="battery-status">
            <span className="battery-icon">{getBatteryIcon()}</span>
            <span 
              className="battery-level" 
              style={{ color: getBatteryColor() }}
            >
              {batteryInfo.level}%
            </span>
            {batteryInfo.charging && <span className="charging-indicator">Charging</span>}
          </div>
        )}
      </div>

      <div className="power-presets">
        <h4>Quick Presets</h4>
        <div className="preset-buttons">
          <button 
            className="preset-btn performance"
            onClick={() => applyPreset('performance')}
          >
            🚀 Performance
          </button>
          <button 
            className="preset-btn balanced"
            onClick={() => applyPreset('balanced')}
          >
            ⚖️ Balanced
          </button>
          <button 
            className="preset-btn power-saver"
            onClick={() => applyPreset('powerSaver')}
          >
            🔋 Power Saver
          </button>
        </div>
      </div>

      <div className="power-settings">
        <div className="setting-group">
          <h4>General Settings</h4>
          
          <div className="setting-item">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={settings.powerSaveMode}
                onChange={(e) => handleSettingChange('powerSaveMode', e.target.checked)}
              />
              <span className="checkmark"></span>
              Enable Power Save Mode
            </label>
            <p className="setting-description">
              Reduces overall system resource usage and extends battery life
            </p>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              Balance Update Interval: {getUpdateIntervalLabel(settings.balanceUpdateInterval)}
            </label>
            <input
              type="range"
              min="30000"
              max="300000"
              step="30000"
              value={settings.balanceUpdateInterval}
              onChange={(e) => handleSettingChange('balanceUpdateInterval', parseInt(e.target.value))}
              className="setting-slider"
            />
            <div className="slider-labels">
              <span>30s</span>
              <span>5min</span>
            </div>
          </div>
        </div>

        <div className="setting-group">
          <h4>Optimization Features</h4>
          
          <div className="setting-item">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={settings.enableVisibilityOptimization}
                onChange={(e) => handleSettingChange('enableVisibilityOptimization', e.target.checked)}
              />
              <span className="checkmark"></span>
              Pause when tab is hidden
            </label>
            <p className="setting-description">
              Automatically pause updates when the browser tab is not visible
            </p>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={settings.enableBatteryOptimization}
                onChange={(e) => handleSettingChange('enableBatteryOptimization', e.target.checked)}
              />
              <span className="checkmark"></span>
              Battery-aware optimization
            </label>
            <p className="setting-description">
              Automatically adjust performance based on battery level
            </p>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={settings.pauseOnLowBattery}
                onChange={(e) => handleSettingChange('pauseOnLowBattery', e.target.checked)}
              />
              <span className="checkmark"></span>
              Pause on low battery
            </label>
            <p className="setting-description">
              Automatically pause non-essential features when battery is low
            </p>
          </div>

          {settings.pauseOnLowBattery && (
            <div className="setting-item sub-setting">
              <label className="setting-label">
                Low battery threshold: {settings.lowBatteryThreshold}%
              </label>
              <input
                type="range"
                min="10"
                max="50"
                step="5"
                value={settings.lowBatteryThreshold}
                onChange={(e) => handleSettingChange('lowBatteryThreshold', parseInt(e.target.value))}
                className="setting-slider"
              />
            </div>
          )}
        </div>

        <div className="setting-group">
          <h4>Performance Settings</h4>
          
          <div className="setting-item">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={settings.reduceAnimations}
                onChange={(e) => handleSettingChange('reduceAnimations', e.target.checked)}
              />
              <span className="checkmark"></span>
              Reduce animations
            </label>
            <p className="setting-description">
              Minimize visual effects to save CPU and battery
            </p>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={settings.limitWebSocketConnections}
                onChange={(e) => handleSettingChange('limitWebSocketConnections', e.target.checked)}
              />
              <span className="checkmark"></span>
              Limit WebSocket connections
            </label>
            <p className="setting-description">
              Reduce the number of real-time connections to save bandwidth and battery
            </p>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={settings.enableBackgroundSync}
                onChange={(e) => handleSettingChange('enableBackgroundSync', e.target.checked)}
              />
              <span className="checkmark"></span>
              Enable background sync
            </label>
            <p className="setting-description">
              Continue syncing data when the app is in the background
            </p>
          </div>
        </div>
      </div>

      <div className="power-info">
        <h4>💡 Power Saving Tips</h4>
        <ul>
          <li>Use "Power Saver" preset when on battery power</li>
          <li>Close unused browser tabs to reduce resource usage</li>
          <li>Reduce screen brightness to extend battery life</li>
          <li>Enable "Pause when tab is hidden" for maximum savings</li>
          <li>Consider using a dedicated trading device when possible</li>
        </ul>
      </div>
    </div>
  );
};

export default PowerManagement;